import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import { ArrowRight, Play } from "lucide-react";
import styles from "./MonitorUI.module.scss";

type ClassNameProps = { className?: string };

export interface MonitorPageProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function MonitorPage({
  children,
  className = "",
  ...props
}: MonitorPageProps) {
  return (
    <div
      className={[styles.page, className].filter(Boolean).join(" ")}
      {...props}
    >
      {children}
    </div>
  );
}

export interface PageHeadingProps extends ClassNameProps {
  title: ReactNode;
  description?: ReactNode;
}

export function PageHeading({
  title,
  description,
  className = "",
}: PageHeadingProps) {
  return (
    <header
      className={[styles.pageHeading, className].filter(Boolean).join(" ")}
    >
      <h1>{title}</h1>
      {description ? <p>{description}</p> : null}
    </header>
  );
}

export interface ModeHeroProps extends ClassNameProps {
  icon: ReactNode;
  status?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  tone?: "baby" | "parent";
  children?: ReactNode;
}

export function ModeHero({
  icon,
  status,
  title,
  description,
  tone = "baby",
  children,
  className = "",
}: ModeHeroProps) {
  return (
    <section
      className={[styles.modeHero, styles[tone], className]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.heroIcon} aria-hidden="true">
        <span className={styles.heroIconCircle}>{icon}</span>
      </div>
      <div className={styles.heroCopy}>
        {status ? <p className={styles.heroStatus}>{status}</p> : null}
        <h1>{title}</h1>
        {description ? (
          <p className={styles.heroDescription}>{description}</p>
        ) : null}
      </div>
      {children ? <div className={styles.heroChildren}>{children}</div> : null}
    </section>
  );
}

type ActionCardTone = "terracotta" | "dark" | "sage";

export interface ActionCardProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  icon?: ReactNode;
  label: ReactNode;
  description?: ReactNode;
  tone?: ActionCardTone;
  loading?: boolean;
  loadingLabel?: ReactNode;
  actionIcon?: "play" | "arrow";
}

export function ActionCard({
  icon,
  label,
  description,
  tone = "terracotta",
  loading = false,
  loadingLabel = "Loading…",
  actionIcon = "play",
  disabled,
  className = "",
  ...props
}: ActionCardProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      className={[
        styles.actionCard,
        styles[tone],
        description ? styles.hasDescription : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      <span className={styles.actionTop} aria-hidden="true">
        <span
          className={[styles.actionIcon, loading ? styles.loadingIcon : ""]
            .filter(Boolean)
            .join(" ")}
        >
          {loading ? <span className={styles.spinner} /> : icon}
        </span>
        <span className={styles.actionSymbol}>
          {actionIcon === "arrow" ? (
            <ArrowRight size={18} />
          ) : (
            <Play size={15} fill="currentColor" />
          )}
        </span>
      </span>
      <span className={styles.actionText}>
        <span className={styles.actionLabel}>
          {loading ? loadingLabel : label}
        </span>
        {description ? (
          <span className={styles.actionDescription}>{description}</span>
        ) : null}
      </span>
    </button>
  );
}

type MonitorButtonVariant = "primary" | "secondary" | "quiet" | "destructive";

export interface MonitorButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: MonitorButtonVariant;
  loading?: boolean;
  loadingLabel?: ReactNode;
  children: ReactNode;
}

export function MonitorButton({
  variant = "primary",
  loading = false,
  loadingLabel = "Loading…",
  disabled,
  className = "",
  children,
  ...props
}: MonitorButtonProps) {
  return (
    <button
      type="button"
      className={[styles.monitorButton, styles[variant], className]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span className={styles.buttonSpinner} aria-hidden="true" />
      ) : null}
      <span>{loading ? loadingLabel : children}</span>
    </button>
  );
}

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  "aria-label": string;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, className = "", type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      className={[styles.iconButton, className].filter(Boolean).join(" ")}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = "IconButton";

type StatusTone = "neutral" | "success" | "warning" | "error";

export interface StatusLineProps extends ClassNameProps {
  tone?: StatusTone;
  children: ReactNode;
}

export function StatusLine({
  tone = "neutral",
  children,
  className = "",
}: StatusLineProps) {
  return (
    <p
      className={[styles.statusLine, styles[tone], className]
        .filter(Boolean)
        .join(" ")}
      role="status"
    >
      <span className={styles.statusMarker} aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

type InlineMessageTone = "error" | "success" | "info";

export interface InlineMessageProps extends ClassNameProps {
  tone?: InlineMessageTone;
  children: ReactNode;
}

export function InlineMessage({
  tone = "error",
  children,
  className = "",
}: InlineMessageProps) {
  return (
    <div
      className={[styles.inlineMessage, styles[tone], className]
        .filter(Boolean)
        .join(" ")}
      role={tone === "error" ? "alert" : "status"}
      aria-live={tone === "error" ? "assertive" : "polite"}
    >
      {children}
    </div>
  );
}

export interface SettingRowProps extends ClassNameProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}

export function SettingRow({
  title,
  description,
  children,
  className = "",
}: SettingRowProps) {
  return (
    <div className={[styles.settingRow, className].filter(Boolean).join(" ")}>
      <div className={styles.settingCopy}>
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {children ? <div className={styles.settingAction}>{children}</div> : null}
    </div>
  );
}

export interface FormFieldProps extends ClassNameProps {
  label: ReactNode;
  htmlFor?: string;
  help?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}

export function FormField({
  label,
  htmlFor,
  help,
  error,
  children,
  className = "",
}: FormFieldProps) {
  const generatedId = useId();
  const helpId = help ? `${generatedId}-help` : undefined;
  const errorId = error ? `${generatedId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(" ") || undefined;
  const childProps = isValidElement(children)
    ? (children.props as {
        id?: string;
        "aria-describedby"?: string;
        "aria-invalid"?: boolean;
      })
    : undefined;
  const controlDescribedBy =
    [childProps?.["aria-describedby"], describedBy].filter(Boolean).join(" ") ||
    undefined;
  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        "aria-describedby": controlDescribedBy,
        "aria-invalid": error ? true : childProps?.["aria-invalid"],
      })
    : children;

  return (
    <div className={[styles.formField, className].filter(Boolean).join(" ")}>
      <label htmlFor={htmlFor ?? childProps?.id}>{label}</label>
      {control}
      {help ? (
        <p id={helpId} className={styles.fieldHelp}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className={styles.fieldError} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
